import {
  Component,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnInit,
  OnChanges,
} from '@angular/core'
import { FormBuilder, Validators, FormGroup } from '@angular/forms'
import get from 'lodash-es/get'
import find from 'lodash-es/find'
import 'moment-timezone'
import moment from 'moment'
import { ValidationService } from '../../../services/validation-service'
import { infoToast, errorToast } from '../../../toaster'
import { externalRoute } from '../external-routes'
import {
  IAccount,
  IKeyword,
  IContentType,
  ICustomer,
  KeywordQueries,
  IWriter,
} from '../../../graphql/queries/keyword.queries'
import { paymentIcon } from 'logic/pricing'
import { waitUntil, quantityChargeDollars } from '../../../utils'
import { TinymceConfig, tinymceConfig } from 'src/tinymce'
import { SeoRequirementsComponent } from 'modules/keywords/keyword-form/seo-requirements.component'
import { DeadlineComponent } from 'modules/keywords/keyword-form/deadline.component'
import { DirectAssignmentComponent } from 'modules/keywords/keyword-form/direct-assignment.component'
import { SaveKeywordDialog } from '../save-keyword-dialog.component'
import {
  hasViableDeadline,
  getViableDeadline,
  getMinDeadlineDate,
  getMaxDeadlineDate,
} from '../utils'
import { availableCredits } from 'logic/subscription-states'
import pluralize from 'pluralize'
import {
  PostCreditPayment,
  PaymentOption,
  KeywordFormPaymentOptionType,
} from 'interfaces/post-credit-payment.interface'
import { placeholderString } from './help-strings'
import sweetAlert from 'src/bmAlert'
import { KeywordMutations } from 'src/graphql/mutations/keyword.mutations'
import { DeclineSubmissionsHelper } from '../helpers/decline-submissions-alerts.helper'

@Component({
  styles: [
    `
      :host {
        display: block;
      }
      .btn-outline-muted {
        border-color: #ced4da !important;
      }
    `,
  ],
  template: `
    <form [formGroup]="form">
      <div class="p-3">
        <!-- COMPONENT HEADER START -->
        <div class="row mt-3 mt-sm-0 d-flex align-items-center">
          <div class="col-sm-3" *ngIf="contentTypesEnabled">
            <div dropdown>
              <div dropdownToggle class="editable-field small-bottom-buffer">
                <button
                  class="btn btn-outline-muted btn-block d-flex justify-content-between align-items-center">
                  <span>{{
                    selectedContentType ? selectedContentType.name : 'Select Content Type'
                  }}</span>
                  <i class="fas fa-caret-down"></i>
                </button>
                <div
                  class="fas fa-exclamation-circle text-danger small-left-buffer"
                  *ngIf="
                    selectedContentType && !contentTypeIsAllowed(selectedContentType.id)
                  "></div>
                <div
                  class="editable-field-remove"
                  *ngIf="
                    selectedContentType && !contentTypeIsAllowed(selectedContentType.id)
                  "></div>
              </div>
              <div *dropdownMenu class="small-left-buffer dropdown-menu" role="menu">
                <a
                  class="dropdown-item"
                  *ngFor="let contentType of allowedContentTypes"
                  (click)="changeContentType(contentType.id)"
                  >{{ contentType.name }}</a
                >
                <a
                  class="dropdown-item"
                  href="https://help.verblio.com/quick-guide-to-verblio/how-to-specify-content-types"
                  target="_blank"
                  >Looking for another type?
                  <i class="tiny-left-buffer fas fa-question-circle text-secondary"></i
                ></a>
              </div>
            </div>
          </div>
          <div class="col-sm-6">
            <label class="font-weight-normal mr-2 small" for="rewrite">
              <input type="checkbox" id="rewrite" name="rewrite" formControlName="rewrite" />
              This is a Content Refresh.
              <a href="https://help.verblio.com/en/articles/6037967-content-refresh" target="_blank"
                >Required steps</a
              >
            </label>
          </div>
          <div class="col-sm-3 d-flex justify-content-end">
            <div dropdown class="btn-group">
              <button class="tiny-left-buffer btn btn-link" dropdownToggle>
                <div class="fas fa-ellipsis-v cursor-pointer text-gray"></div>
              </button>
              <div *dropdownMenu class="dropdown-menu dropdown-menu-right">
                <a
                  role="menuitem"
                  class="dropdown-item"
                  href="{{ linkTo('improve_submissions') }}"
                  target="_blank">
                  Improve Submissions
                  <i class="fas fa-question-circle tiny-left-buffer"></i>
                </a>
                <a
                  *ngIf="viewingKeyword()"
                  role="menuitem"
                  class="dropdown-item cursor-pointer"
                  (click)="onStartDuplication()">
                  Duplicate
                </a>
                <a
                  *ngIf="keyword && keyword.state != 'deleted'"
                  role="menuitem"
                  class="dropdown-item cursor-pointer"
                  (click)="onDelete()">
                  Delete Request
                </a>
              </div>
            </div>
          </div>
        </div>
        <!-- COMPONENT HEADER END -->

        <div class="row mt-1">
          <!-- EDITOR COLUMN LEFT START -->
          <div class="col-sm-8">
            <!-- TITLE INPUT START -->
            <div class="row">
              <div class="col-12">
                <div class="order-sm-first d-flex flex-column top-buffer no-top-buffer-md mb-3">
                  <input
                    placeholder="Your Title"
                    data-ls-enabled
                    formControlName="phrase"
                    class="form-control keyword-phrase-input" />
                </div>
              </div>
            </div>
            <!-- TITLE INPUT END -->

            <!-- TINYMCE EDITOR START -->
            <div class="row">
              <div class="col-12">
                <editor
                  #editorEl
                  (onFocus)="populatePlaceholderTemplate(true)"
                  (onBlur)="populatePlaceholderTemplate(false)"
                  [ngModel]="form.value.description"
                  (ngModelChange)="updateDescription($event)"
                  [ngModelOptions]="{ standalone: true }"
                  name="description"
                  data-ls-enabled="true"
                  [init]="tinymceConfig">
                </editor>
              </div>
            </div>
            <!-- TINYMCE EDITOR END -->
          </div>
          <!-- EDITOR COLUMN LEFT END -->

          <!-- OPTIONS COLUMN RIGHT START -->
          <div class="col-sm-4">
            <!-- REQUIRE MY EXACT TITLE START-->
            <div class="row mt-3 mt-md-1">
              <div class="col-12">
                <label class="font-weight-normal mb-0 small" for="requireSameTitle">
                  <input
                    type="checkbox"
                    id="requireSameTitle"
                    name="requireSameTitle"
                    formControlName="requireSameTitle" />
                  Require my exact title
                  <i
                    class="fas fa-question-circle tiny-left-buffer"
                    popover="Leave this unchecked if you want the writer to provide your title."
                    triggers="mouseover:mouseout"></i>
                </label>
              </div>
            </div>
            <!-- REQUIRE MY EXACT TITLE END -->

            <!-- ALLOW INSECURE LINKS START -->
            <div class="row mt-3">
              <div class="col-12">
                <label class="font-weight-normal mb-0 small" for="allowInsecureLinks">
                  <input
                    type="checkbox"
                    id="allowInsecureLinks"
                    name="allowInsecureLinks"
                    formControlName="allowInsecureLinks" />
                  Allow insecure (HTTP) links
                  <i
                    class="fas fa-question-circle tiny-left-buffer"
                    popover="Writers are usually required to use secure (HTTPS) links, in response to Google's mixed content security standards"
                    triggers="mouseover:mouseout"></i>
                </label>
              </div>
            </div>
            <!-- ALLOW INSECURE LINKS END -->

            <!-- SEO OPTIONS START -->
            <div class="row mt-3" *ngIf="showSeoRequirements">
              <div class="col-12">
                <hr class="d-md-none mt-2 small-bottom-buffer" />
                <seo-requirements [form]="form" #seoRequirementsComp></seo-requirements>
              </div>
            </div>
            <!-- SEO OPTIONS END -->

            <!-- PRIORITY PICKER START -->
            <div class="row mt-3" *ngIf="onePost() && !shouldShowDeadlinePicker()">
              <div class="col-12 d-flex justify-content-start" #pickerAnchor>
                <div
                  class="d-flex align-items-center"
                  [class.d-none]="form.value.deadline || deadlinePickerVisible"
                  dropdown
                  (isOpenChange)="priorityActive = $event"
                  container="body">
                  <span dropdownToggle id="priority-picker-anchor" class="editable-field full">
                    <div class="editable-field-icon tiny-right-buffer">
                      <i class="fas fa-arrows-alt-v"></i>
                    </div>
                    <span>{{ priorities[form.value.priority] }}</span>
                  </span>
                  <div *dropdownMenu class="dropdown-menu">
                    <a
                      *ngFor="let priorityText of priorities; index as priority"
                      role="menuitem"
                      class="dropdown-item"
                      (click)="updatePriority(priority)"
                      [class.active]="form.value.priority === priority"
                      >{{ priorityText }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <!-- PRIORITY PICKER END -->

            <!-- DIRECT ASSIGNMENTS START -->
            <div class="row mt-3" *ngIf="shouldShowDirectAssignmentsPicker()">
              <div class="col-12">
                <div class="d-flex align-items-center">
                  <div *ngIf="onePost(); else blockedDirectAssignmentPicker">
                    <direct-assignment
                      [customer]="customer"
                      [enabled]="directAssignmentsEnabled"
                      [preassignedWriter]="preassignedWriter"
                      [keyword]="keyword"
                      (assignToWriter)="onAssignWriter($event)"
                      (removeAssignment)="removeAssignment()"
                      #directAssignmentComp>
                    </direct-assignment>
                  </div>
                  <ng-template #blockedDirectAssignmentPicker>
                    <div class="editable-field empty default-pointer">
                      <i
                        class="editable-field-icon fas fa-user tiny-right-buffer text-disabled"></i>
                      <span
                        class="text-disabled"
                        popover="Not available when requesting more than 1 piece"
                        triggers="mouseover:mouseout"
                        >Assign preferred writer</span
                      >
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>
            <!-- DIRECT ASSIGNMENTS END -->

            <!-- DEADLINE PICKER START -->
            <div class="row mt-3" *ngIf="shouldShowDeadlinePicker()">
              <div class="col-12">
                <div *ngIf="onePost(); else blockedDeadlinePicker">
                  <deadline
                    [form]="form"
                    [keyword]="keyword"
                    [minDeadlineDate]="minDeadlineDate"
                    [maxDeadlineDate]="maxDeadlineDate"
                    (closeDeadlineDialog)="closeDeadlineDialog()"
                    #deadlineComp></deadline>
                </div>
                <ng-template #blockedDeadlinePicker>
                  <div class="editable-field empty default-pointer">
                    <i
                      class="editable-field-icon fas fa-calendar tiny-right-buffer text-disabled"></i>
                    <span
                      class="text-disabled"
                      popover="Not available when requesting more than 1 piece"
                      triggers="mouseover:mouseout"
                      >Add deadline</span
                    >
                  </div>
                </ng-template>
              </div>
            </div>
            <div class="row mt-3" *ngIf="directAssignmentsEnabled && assignedWithoutDeadline">
              <div class="col-12">
                <div class="alert alert-danger small">
                  <b>A deadline is required</b> to assign one of your preferred writers
                </div>
              </div>
            </div>
            <!-- DEADLINE PICKER END -->

            <!-- PDF UPLOADER START -->
            <div class="row mt-3">
              <div class="col-12">
                <file-manager
                  [form]="form"
                  [duplicateId]="duplicateId"
                  [keyword]="keyword"
                  [tempFiles]="tempFiles">
                </file-manager>
              </div>
            </div>
            <!-- PDF UPLOADER END -->
          </div>
          <!-- OPTIONS COLUMN RIGHT END-->
        </div>

        <hr class="d-sm-none my-2" />

        <!-- POST LIMIT INPUT ROW START -->
        <div *ngIf="customer" class="row mt-3">
          <div *ngIf="!canChangePostLimit" class="col-12 col-lg 8">
            {{ creditUsageSummary }}
            <ng-template #summaryTooltip>
              <strong>Need to change the quantity or payment method for this request?</strong>
              Please deactivate the request to receive credits back, and click "Activate Request" to
              start a new request with the correct details.
            </ng-template>
            <i
              class="fas fa-question-circle ml-1"
              containerClass="white-tooltip"
              [tooltip]="summaryTooltip"></i>
          </div>
          <div
            *ngIf="canChangePostLimit"
            class="col-12 col-lg-8 d-flex flex-row align-items-center">
            I need
            <div
              containerClass="white-tooltip"
              [tooltip]="postLimitTooltipMessage"
              class="ml-2 mr-2 w-10">
              <input
                id="postLimitInput"
                type="number"
                class="form-control text-center number-input-wo-arrows"
                [disabled]="!!assignedWriter"
                min="1"
                max="99"
                (change)="validatePiecesLimit($event)"
                (keyup)="validatePiecesLimit($event)"
                [value]="form.value.postLimit" />
            </div>
            {{ 'piece' | pluralize: form.value.postLimit }} on this topic. I'll pay with
            <div dropdown class="ml-2 flex-grow-1 btn-group" placement="bottom right">
              <button
                dropdownToggle
                class="btn btn-outline-muted btn-block d-flex justify-content-between align-items-center bg-white">
                <span *ngIf="!selectedPaymentOption" class="small">
                  <i class="fa fa-exclamation-circle text-danger mr-1"></i>
                  make a selection
                </span>
                <span *ngIf="selectedPaymentOption">{{
                  selectedPaymentOption.optionPlaceholder
                }}</span>
                <i class="fas fa-caret-down"></i>
              </button>
              <div *dropdownMenu class="dropdown-menu mb-3" role="menu">
                <a
                  *ngFor="let option of paymentOptions"
                  [class.disabled]="option.optionDisabled"
                  (click)="selectPaymentOption(option.optionType)"
                  class="dropdown-item cursor-pointer">
                  <strong>{{ option.optionText }}</strong
                  ><br />
                  <small>
                    <i *ngIf="option.optionType === 'charge'" class="{{ paymentIcon() }}"> </i>
                    {{ option.optionDescription }}
                  </small>
                </a>
              </div>
            </div>

            <i
              class="fas fa-question-circle ml-2"
              containerClass="white-tooltip"
              tooltip="Once activated you will not be able to change the quantity or payment method for this request. You can always delete or deactivate the request to receive credits back."></i>
          </div>
        </div>
        <!-- POST LIMIT INPUT ROW END -->

        <!-- COMPONENT FOOTER (BUTTONS & MSGs) START -->
        <div class="row top-buffer">
          <div class="col-12" *ngIf="isEditable(); else startDuplication">
            <button
              (click)="onSaveDraft()"
              class="btn btn-outline-primary btn-block-sm-down"
              id="save-draft-btn"
              [disabled]="submitting">
              {{ draftButtonText }}
            </button>
            <button
              id="activate-keyword-btn"
              (click)="onSubmit()"
              class="btn btn-primary mt-2 mt-md-0 ml-md-2 btn-block-sm-down"
              [disabled]="activationDisabled || submitting">
              <span *ngIf="!keyword || keyword?.state == 'paused' || duplicateId; else updating"
                >Activate Request</span
              >
              <ng-template #updating>
                <span *ngIf="reactivatingKeyword()">Reactivate Request</span>
                <span *ngIf="updatingCurrentKeyword()">Update Request</span>
              </ng-template>
            </button>
            <button
              (click)="onCancel()"
              class="d-none d-md-block float-right btn btn-link no-decoration">
              Cancel
            </button>
          </div>
          <ng-template #startDuplication>
            <div class="col-12">
              <button
                class="btn btn-outline-primary btn-block-sm-down"
                id="save-as-new-btn"
                (click)="onStartDuplication()">
                Duplicate
              </button>
              <button
                (click)="onCancel()"
                class="d-none d-md-block float-right btn btn-link no-decoration">
                Cancel
              </button>
            </div>
          </ng-template>
          <div class="col-12" *ngIf="hasClaimedFulfilledKeyword">
            <span class="text-muted small"
              >A writer has already fulfilled this deadline. Please create a new request.</span
            >
          </div>

          <div
            class="col top-buffer"
            *ngIf="
              selectedContentType && contentTypeIsAllowed(selectedContentType.id);
              else contentTypeWarning
            ">
            <hr class="tiny-top-buffer tiny-bottom-buffer" />
            <span class="text-muted small">
              {{ draftHelperText }}
            </span>
          </div>
          <ng-template #contentTypeWarning>
            <div *ngIf="contentTypesEnabled">
              <div class="col top-buffer">
                <hr class="tiny-top-buffer tiny-bottom-buffer" />
                <div class="fas fa-exclamation-circle text-danger small-right-buffer"></div>
                <span class="text-muted small">
                  This request can not be activated at your current word-length. Choose a compatible
                  content type or upgrade your subscription.
                </span>
              </div>
            </div>
          </ng-template>

          <div class="d-block d-md-none col d-flex flex-row justify-content-end align-items-end">
            <button (click)="onCancel()" class="float-right btn btn-link no-decoration">
              Cancel
            </button>
          </div>
        </div>
        <!-- COMPONENT FOOTER END -->
      </div>
    </form>
  `,
  selector: 'keyword-form',
})
export class KeywordFormComponent
  implements OnInit, OnChanges, PostCreditPayment<KeywordFormPaymentOptionType>
{
  @Input() keyword: IKeyword = null
  @Input() account: IAccount = null // Deadline flipper feature & billing info
  @Input() customer: ICustomer = null
  @Input() allContentTypes: IContentType[] = null
  @Input() allowedContentTypes: IContentType[] = null

  @Input() contentTypeId: number
  @Input() duplicateId: number = null
  @Input() submitting: boolean

  @Output() create: EventEmitter<{ body: object; files: File[] }> = new EventEmitter<{
    body: object
    files: File[]
  }>()
  @Output() duplicate: EventEmitter<{ body: object; documents: any[] }> = new EventEmitter<{
    body: object
    documents: any[]
  }>()
  @Output() update: EventEmitter<object> = new EventEmitter<object>()
  @Output() cancel: EventEmitter<null> = new EventEmitter<null>()
  @Output() delete: EventEmitter<null> = new EventEmitter<null>()
  @Output() startDuplication: EventEmitter<null> = new EventEmitter<null>()

  @ViewChild('editorEl') editorEl: ElementRef<any>
  @ViewChild('pickerAnchor') pickerAnchor: ElementRef<any>
  @ViewChild('seoRequirementsComp') seoRequirements: SeoRequirementsComponent
  @ViewChild('deadlineComp') deadlineDialog: DeadlineComponent
  @ViewChild('directAssignmentComp') directAssignmentDialog: DirectAssignmentComponent
  tempFiles: File[] = []

  deadlinePickerVisible: boolean = false
  calendarVisible: boolean = false
  editingPostLimit: boolean = false
  editingSeoRequirements: boolean = false
  showSeoRequirements: boolean = false

  assignedWriter: IWriter

  tinymcePlaceholder = ''

  tinymceConfig: TinymceConfig = tinymceConfig(<TinymceConfig>{
    placeholder: this.tinymcePlaceholder,
    toolbar: 'bold italic underline | numlist bullist | link | blockquote',
    statusbar: true,
    elementpath: false,
    resize: true,
  })

  form: FormGroup
  tempSeoRequirements: FormGroup
  // Need to use more "temporary" placeholders instead of direct form binding, because user needs to save choice

  selectedContentType: IContentType = null

  minDeadlineDate: Date
  maxDeadlineDate: Date

  priorities: string[] = ['Low Priority', 'Medium Priority', 'High Priority']
  paymentOptions: PaymentOption<KeywordFormPaymentOptionType>[] = []
  selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType> = null

  constructor(
    private fb: FormBuilder,
    private keywordQueries: KeywordQueries,
    private keywordMutations: KeywordMutations
  ) {}

  ngOnInit(): void {
    if (this.duplicateId) {
      this.keywordQueries
        .duplicateKeywordPageQuery(this.customer.id, +this.duplicateId)
        .subscribe(({ data }) => {
          if (data && !this.keyword) {
            // do this only once
            this.keyword = data.customer.keyword
            this.resetForm().then(() => {
              this.keyword.id = null // New kw
              this.form.markAsDirty()
              this.updatePaymentOptions()
              const defaultOptionType = this.paymentOptions.find(
                (o) => !o.optionDisabled
              ).optionType
              this.selectPaymentOption(defaultOptionType)
            })
          }
        })
    } else {
      this.resetForm()
    }
    this.waitForPlaceholder()
      .then((_el) => this.changeContentType(this.form.value.contentTypeId))
      .catch((e) => console.log('Timed out waiting for TinyMCE placeholder', e))
  }

  // Update the form if the input keyword changes from null
  ngOnChanges(changes: SimpleChanges): void {
    for (let [_key, value] of Object.entries(changes)) {
      if (value.previousValue == null || value.previousValue == undefined) {
        this.resetForm().then(() => {
          const postLimit = this.form.value.postLimit
          if (this.customer && postLimit) {
            this.updatePaymentOptions()
            const paymentOptions = this.paymentOptions
              .filter((o) => !o.optionDisabled)
              .map((o) => o.optionType)
            const preferredPaymentOption =
              paymentOptions.find((o) => o === 'credits') ||
              paymentOptions.find((o) => o === 'mixed-payment') ||
              paymentOptions.find((o) => o === 'charge')
            this.selectPaymentOption(preferredPaymentOption)
          }
        })
      }
    }
  }
  waitForPlaceholder = waitUntil(() => document.querySelector('editor placeholder'), 100, 2000)

  get preassignedWriter(): IWriter {
    const assignedKeyword = get(this.keyword, 'kind') === 'assigned'
    const writer = get(this.keyword, 'writer')
    return assignedKeyword && writer
  }

  resetForm(): Promise<void> {
    const defaults = {
      kind: get(this.keyword, 'kind', 'open'),
      phrase: this.duplicateId
        ? '[Copy of] ' + get(this.keyword, 'phrase', '')
        : get(this.keyword, 'phrase', ''),
      description: get(this.keyword, 'description', ''),
      deadline: this.duplicateId
        ? this.getSensibleDuplicateDeadline(this.keyword)
        : get(this.keyword, 'deadline')
        ? new Date(this.keyword.deadline)
        : null,
      timeZone: this.deadlineTimeZone(),
      priority: get(this.keyword, 'priority', 1),
      state: get(this.keyword, 'state', 'active'),
      postLimit: get(this.keyword, 'postLimit', 1) || 1,
      contentTypeId: this.contentTypeId,
      focusKeywords: get(this.keyword, 'focusKeywords', ''),
      link: get(this.keyword, 'link', ''),
      linkText: get(this.keyword, 'linkText', ''),
      externalLinks: get(this.keyword, 'externalLinks', null),
      searcherIntent: get(this.keyword, 'searcherIntent', ''),
      seoNotes: get(this.keyword, 'seoNotes', ''),
      contentType: get(this.keyword, 'contentTypeId', 1),
      rewrite: get(this.keyword, 'rewrite', false),
      writerId: this.duplicatingCurrentKeyword() ? null : get(this.keyword, 'writerId', null),
      allowInsecureLinks: get(this.keyword, 'allowInsecureLinks', false),
      requireSameTitle: get(this.keyword, 'requireSameTitle', false) || false,
      quantityFromCredits: get(this.keyword, 'quantityFromCredits', 1),
    }
    this.form = this.fb.group({
      kind: [defaults.kind, Validators.required],
      phrase: [defaults.phrase, Validators.required],
      description: defaults.description,
      deadline: defaults.deadline,
      timeZone: defaults.timeZone,
      priority: defaults.priority,
      state: [defaults.state, Validators.required],
      postLimit: defaults.postLimit,
      contentTypeId: defaults.contentTypeId,
      writerId: defaults.writerId,
      quantityFromCredits: defaults.quantityFromCredits,

      // SEO Options
      focusKeywords: defaults.focusKeywords,
      link: [defaults.link, ValidationService.urlValidator],
      linkText: defaults.linkText,
      externalLinks: defaults.externalLinks,
      searcherIntent: defaults.searcherIntent,
      seoNotes: defaults.seoNotes,
      rewrite: defaults.rewrite,
      allowInsecureLinks: defaults.allowInsecureLinks,
      requireSameTitle: defaults.requireSameTitle,
    })
    this.changeContentType(this.form.value.contentTypeId)
    this.setDeadlineBuffers()

    if (this.keyword && this.keyword.kind === 'assigned' && this.keyword.writer)
      this.deadlinePickerVisible = true
    return Promise.resolve()
  }

  updatePaymentOptions(): void {
    let mixedPaymentCredits = this.form.value.postLimit
    let mixedPaymentAmount = 0
    if (this.form.value.postLimit > this.customer?.postCreditsCount) {
      mixedPaymentCredits = this.customer.postCreditsCount
      mixedPaymentAmount = this.form.value.postLimit - this.customer.postCreditsCount
    }

    const mixedPaymentDisabled = this.form.value.postLimit < this.customer.postCreditsCount
    const creditsBackMsg =
      'You can always deactivate or delete your request to receive credits back.'
    const paypal = this.account.billingType === 'paypal'

    this.paymentOptions = [
      {
        optionType: 'credits',
        optionDisabled: false,
        optionText: `Pay ${this.form.value.postLimit} ${pluralize(
          'credit',
          this.form.value.postLimit
        )}`,
        optionDescription: this.optionValidForSelection('credits')
          ? 'Save 5% by using subscription credits'
          : 'Not enough credits for this method',
        optionPlaceholder: `Pay ${this.form.value.postLimit} ${pluralize(
          'credit',
          this.form.value.postLimit
        )}`,
        confirmMessage:
          `You will pay ${this.form.value.postLimit} ${pluralize(
            'credit',
            this.form.value.postLimit
          )}. ` + creditsBackMsg,
      },
      {
        optionType: 'mixed-payment',
        optionDisabled: true,
        optionText: `Pay ${mixedPaymentCredits} ${pluralize(
          'credit',
          mixedPaymentCredits
        )} + charge ${this.getTotalAmount(mixedPaymentAmount) ?? '$0'}`,
        optionDescription: (() => {
          if (mixedPaymentDisabled) {
            return 'Mixed payment not available'
          } else if (paypal) {
            return `Spend your credits + charge the remainder to linked PayPal account.`
          } else {
            return `Spend your credits + charge the remainder to card ending ${this.account.lastFour}`
          }
        })(),
        optionPlaceholder: `${mixedPaymentCredits} ${pluralize(
          'credit',
          mixedPaymentCredits
        )} + ${this.getTotalAmount(mixedPaymentAmount)}`,
        confirmMessage:
          (paypal
            ? `Pay ${mixedPaymentCredits} ${pluralize(
                'credit',
                mixedPaymentCredits
              )} + charge ${this.getTotalAmount(
                mixedPaymentAmount
              )} to linked <i class="${this.paymentIcon()}"></i> <b>PayPal</b> account. `
            : `Pay ${mixedPaymentCredits} ${pluralize(
                'credit',
                mixedPaymentCredits
              )} + charge ${this.getTotalAmount(
                mixedPaymentAmount
              )} to <i class="${this.paymentIcon()}"></i> ending in <b>${
                this.account.lastFour
              }</b>. `) + creditsBackMsg,
      },
      {
        optionType: 'charge',
        optionDisabled: false,
        optionText: `Pay ${this.getTotalAmount(this.form.value.postLimit)}`,
        optionDescription: paypal
          ? `One-time charge to linked PayPal account`
          : `One-time charge to card ending ${this.account?.lastFour}`,
        optionPlaceholder: this.getTotalAmount(this.form.value.postLimit),
        confirmMessage:
          (paypal
            ? `Charge ${this.getTotalAmount(
                this.form.value.postLimit
              )} to linked <i class="${this.paymentIcon()}"></i> <b>PayPal</b> account. `
            : `Charge ${this.getTotalAmount(
                this.form.value.postLimit
              )} to <i class="${this.paymentIcon()}"></i> ending in <b>${
                this.account.lastFour
              }</b>. `) + creditsBackMsg,
      },
    ]

    this.paymentOptions.forEach(
      (o) => (o.optionDisabled = !this.optionValidForSelection(o.optionType))
    )
  }

  getTotalAmount(pieces: number): string {
    return quantityChargeDollars(pieces, this.customer?.subscription.extraPostPrice)
  }

  isEditable(): boolean {
    return (
      this.newKeyword() ||
      (this.keyword &&
        (!['draft', 'deleted', 'fulfilled'].includes(this.keyword.state) ||
          this.duplicateId !== null))
    )
  }

  onStartDuplication(): void {
    this.form.markAsPristine()
    this.startDuplication.emit()
  }

  selectText(): void {
    if (this.htmlToText(this.form.value.description) === this.htmlToText(this.tinymcePlaceholder)) {
      const ed = get(this.editorEl, 'editor', null)
      if (ed) {
        if (ed.selection) {
          setTimeout(() => {
            ed.selection.select(ed.getBody())
          }, 0)
        }
      }
    }
  }

  populatePlaceholderTemplate(populate: boolean): void {
    if (populate && !this.form.value.description) {
      this.form.patchValue({ description: this.tinymcePlaceholder })
      this.selectText()
    } else if (!populate && this.tinymcePlaceholder) {
      if (
        this.htmlToText(this.tinymcePlaceholder) === this.htmlToText(this.form.value.description)
      ) {
        this.form.controls.description.reset('')
      }
    }
  }

  // DEADLINES

  shouldShowDeadlinePicker(): boolean {
    return this.deadlineFeature() || (this.keyword && this.keyword.deadline !== null)
  }

  getSensibleDuplicateDeadline(keyword: IKeyword): Date {
    if (!keyword || !keyword.deadline) return null
    const deadline = new Date(keyword.deadline)
    const deadlineBufferHours = this.customer.deadlineBufferHours
    if (hasViableDeadline(keyword, deadlineBufferHours)) {
      return deadline
    } else {
      return getViableDeadline(deadlineBufferHours)
    }
  }

  deadlineTimeZone(): string {
    return get(this.keyword, 'timeZone', moment.tz.guess() || 'America/Denver')
  }

  closeDeadlineDialog(): void {
    if (!this.assignedWriter) this.deadlinePickerVisible = false
  }

  removeDeadline(): void {
    this.form.value.deadline = null
    this.form.patchValue({ deadline: null })
  }

  // DIRECT ASSIGNMENTS

  shouldShowDirectAssignmentsPicker(): boolean {
    return this.directAssignmentsEnabled || (this.keyword && this.keyword.writer !== null)
  }

  onAssignWriter(writer: IWriter): void {
    this.assignedWriter = writer
    if (this.deadlineDialog && !this.form.value.deadline) this.deadlineDialog.onShowDeadlinePicker()
    this.deadlinePickerVisible = true
  }

  removeAssignment(): void {
    this.assignedWriter = null

    // need to maintain deadline claimer if deadline keyword
    const assignedKeyword = this.keyword?.kind === 'assigned'
    this.form.patchValue({
      writer: assignedKeyword ? null : this.keyword?.writer,
      writerId: assignedKeyword ? null : this.keyword?.writerId,
    })

    this.deadlinePickerVisible = false
  }

  get assignedWithoutDeadline(): boolean {
    return !!this.assignedWriter && !this.form.value.deadline
  }

  // END DIRECT ASSIGNMENTS

  private htmlToText(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return (div.textContent || div.innerText || '').replace(/\n|\r|\s+/g, '').trim()
  }

  onePost(): boolean {
    return this.form.value.postLimit === 1
  }

  multiplePosts(): boolean {
    return this.form.value.postLimit !== 1
  }

  activating(newState: string): boolean {
    const activeStates = ['active', 'assigned']
    return activeStates.includes(newState) && !activeStates.includes(this.keyword.state)
  }

  reactivatingKeyword(): boolean {
    return this.keyword && !['active', 'paused', 'assigned'].includes(this.keyword.state)
  }

  updatingCurrentKeyword(): boolean {
    return this.keyword && ['active', 'paused', 'assigned'].includes(this.keyword.state)
  }

  duplicatingCurrentKeyword(): boolean {
    return this.keyword && this.duplicateId !== null
  }

  viewingKeyword(): boolean {
    return this.keyword && this.duplicateId === null
  }

  newKeyword(): boolean {
    return !this.keyword || !this.keyword.id
  }

  // a new assignment is one that needs to be accepted by the writer
  newAssignment(): boolean {
    const w = this.assignedWriter
    return (
      w &&
      (this.newKeyword() ||
        this.keyword.writerId !== w.id ||
        ['paused', 'declined', 'auto_declined'].includes(this.keyword.state))
    )
  }

  async onSaveDraft(): Promise<void> {
    const cancelConfirm = await DeclineSubmissionsHelper.showDeactivateRequestSweetAlert()
    if (cancelConfirm && this.keyword && this.keyword.id && this.keyword.queuedCount > 0) {
      const confirmDeclineSubmissions =
        await DeclineSubmissionsHelper.showDeclineInactiveSubmissionsSweetAlert(
          this.keyword.queuedCount
        )
      if (confirmDeclineSubmissions)
        this.keywordMutations.declineRemainingKeywordSubmissions({
          id: this.keyword.id,
        })
    }
    if (cancelConfirm) this.onSubmit('paused')
  }

  onSubmit(newState: string = 'active'): void {
    this.submitting = true
    let isActivatingButActivationIsDisabled = newState != 'paused' && this.activationDisabled
    let isDraftButNotEditable = newState == 'paused' && !this.isEditable

    if (isActivatingButActivationIsDisabled || isDraftButNotEditable) {
      infoToast('Activation of this request is not allowed')
      this.submitting = false
      return
    }

    // Tried reactivating before incrementing to postLimit
    if (
      this.reactivatingKeyword() &&
      this.multiplePosts() &&
      this.form.value.postLimit &&
      this.form.value.postLimit <= this.keyword.postedCount
    ) {
      // this.openPostLimitDialog() Do something new?
    } else {
      this.form.patchValue({ state: newState, priority: Number.parseInt(this.form.value.priority) })
      if (this.assignedWriter) {
        this.form.patchValue({
          kind: 'assigned',
          writerId: this.assignedWriter.id,
          priority: 1,
        })
        // this line is VERY sensitive logic - change with caution!
        if (
          newState === 'active' &&
          (this.newAssignment() ||
            (this.keyword && ['assigned', 'active', 'fulfilled'].includes(this.keyword.state)))
        ) {
          this.form.patchValue({ state: 'assigned' })
        }
      } else if (this.form.value.deadline) {
        this.form.patchValue({ kind: 'deadline', priority: 1 })
      } else {
        this.form.patchValue({ kind: 'open' })
      }

      if (this.newKeyword()) {
        this.onCreate()
      } else if (this.reactivatingKeyword() || this.updatingCurrentKeyword()) {
        this.onUpdate()
      }
    }
  }

  onCreate(): void {
    if (this.duplicateId) {
      const createObj = {
        body: this.form.value,
        documents: this.keyword?.documents,
        files: this.tempFiles,
        selectedPaymentOption: this.selectedPaymentOption,
      }
      this.duplicate.emit(createObj)
    } else {
      const createObj = {
        body: this.form.value,
        files: this.tempFiles,
        selectedPaymentOption: this.selectedPaymentOption,
      }
      this.create.emit(createObj)
    }
  }

  onUpdate(): void {
    const updateObj = {
      body: this.form.value,
      selectedPaymentOption: this.selectedPaymentOption,
    }
    this.update.emit(updateObj)
  }

  onDelete(): void {
    this.delete.emit(null)
  }

  // Post limit
  validatePiecesLimit(e: Event): void {
    const postLimit = parseInt((e.target as HTMLInputElement).value)

    if (postLimit > 0 && postLimit < 100) {
      this.setPostLimit(postLimit)
    } else if (postLimit >= 100) {
      errorToast('Cannot be greater than 99')
      this.setPostLimit(99)
    } else {
      errorToast('Cannot be less than 1')
      this.setPostLimit(1)
    }

    this.updatePaymentOptions()
    if (this.selectedPaymentOption) {
      this.selectPaymentOption(this.selectedPaymentOption.optionType)
    } else if (postLimit <= this.customer?.postCreditsCount) {
      const defaultOptionType = this.paymentOptions.find((o) => !o.optionDisabled).optionType
      this.selectPaymentOption(defaultOptionType)
    }
  }

  setPostLimit(postLimit: number) {
    if (postLimit > 1) {
      this.removeAssignment()
      this.removeDeadline()
    }

    this.form.patchValue({ postLimit })
  }

  selectPaymentOption(selectedOptionType: KeywordFormPaymentOptionType): void {
    if (!this.optionValidForSelection(selectedOptionType)) {
      this.selectedPaymentOption = null
      this.form.patchValue({ quantityFromCredits: null })
      return
    }

    this.selectedPaymentOption = this.paymentOptions.find((o) => o.optionType == selectedOptionType)

    let quantityFromCredits
    switch (this.selectedPaymentOption?.optionType) {
      case 'charge':
        quantityFromCredits = 0
        break
      case 'credits':
        quantityFromCredits = this.form.value.postLimit
        break
      case 'mixed-payment':
        quantityFromCredits = this.customer?.postCreditsCount
        break
      default:
        quantityFromCredits = null
    }

    this.form.patchValue({ quantityFromCredits })
  }

  optionValidForSelection(selectedOptionType: KeywordFormPaymentOptionType): boolean {
    const { postLimit } = this.form.value
    const numCredits = availableCredits(this.customer)

    switch (selectedOptionType) {
      case 'charge':
        return true
      case 'credits':
        return numCredits >= postLimit
      case 'mixed-payment':
        return numCredits > 0 && numCredits < postLimit
    }
  }

  paymentIcon(): string {
    return paymentIcon(this.account)
  }

  updatePriority(newPriority: number): void {
    this.form.patchValue({ priority: newPriority })
  }

  // Can't use reactive forms. Angular sometimes can't tell on data reloads that tinymce is in the frame
  updateDescription(newDescription: string): void {
    const descriptionChanged = newDescription !== this.form.get('description').value
    this.form.patchValue({ description: newDescription })
    if (descriptionChanged) this.form.controls.description.markAsDirty()
  }

  onCancel(): void {
    this.cancel.emit(null)
  }

  linkTo(externalRouteKey: string): string {
    return externalRoute(externalRouteKey)
  }

  deadlineFeature(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return features.includes('deadlines') || features.includes('direct_assignments')
  }

  changeContentType(newContentTypeId: number): void {
    if (!this.form.value.description) this.form.patchValue({ description: '' })
    this.form.patchValue({ contentTypeId: newContentTypeId })
    this.selectedContentType = find(this.allContentTypes, { id: newContentTypeId })
    this.tinymcePlaceholder = placeholderString[newContentTypeId] ?? null
    const placeHolderEl = document.querySelector('editor placeholder')
    if (placeHolderEl) placeHolderEl.innerHTML = this.tinymcePlaceholder
    if ([1, 4].includes(newContentTypeId)) {
      this.showSeoRequirements = true
    } else {
      if (this.showSeoRequirements) {
        this.seoRequirements.resetSeoRequirements()
        this.seoRequirements.onSubmitSeoRequirements()
        this.showSeoRequirements = false
      }
    }
  }

  contentTypeIsAllowed(contentTypeId: number): boolean {
    return this.allowedContentTypes.some((type) => {
      return type.id === contentTypeId
    })
  }

  get contentTypesEnabled(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return features.includes('content_types') && get(this.customer, 'plan.words', false)
  }

  get directAssignmentsEnabled(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return features.includes('direct_assignments')
  }

  get draftButtonText(): string {
    const keywordState = get(this.keyword, 'state', null)
    switch (keywordState) {
      case 'active':
      case 'assigned':
        return this.duplicateId ? 'Save & exit' : 'Deactivate'
      case 'fulfilled':
      case 'deleted':
        return 'Save as Draft'
      // 'paused' or creating new keyword
      default:
        return 'Save & exit'
    }
  }

  get draftHelperText(): string {
    const keywordState = get(this.keyword, 'state', null)
    switch (keywordState) {
      case 'active':
      case 'assigned':
        return 'This request is active and open for writer submissions. Deactivate to stop receiving new content.'
      case 'fulfilled':
      case 'deleted':
        return 'Drafts are Inactive and visible only to you and your team. Activate to request submissions from writers.'
      // 'paused' or creating new keyword
      default:
        return 'Progress will be saved as Inactive, visible only to you and your team. Activate to request submissions from writers.'
    }
  }

  get activationDisabled(): boolean {
    return (
      (this.selectedContentType && !this.contentTypeIsAllowed(this.selectedContentType.id)) ||
      this.hasClaimedFulfilledKeyword ||
      this.assignedWithoutDeadline ||
      !this.hasValidDeadline ||
      !this.selectedPaymentOption
    )
  }

  get hasValidDeadline(): boolean {
    if (
      this.form.value.deadline === null ||
      (!this.deadlineChanged && this.keyword.state !== 'paused' && !this.reactivatingKeyword())
    ) {
      return true
    }

    const minDate = new Date(this.minDeadlineDate)

    const minDateInt = minDate.setHours(minDate.getHours() - 1)

    const minDateAllowed = this.form.value.deadline > minDateInt
    const maxDateAllowed = this.form.value.deadline < this.maxDeadlineDate

    return minDateAllowed && maxDateAllowed
  }

  get deadlineChanged(): boolean {
    const formDeadline = this.form.value.deadline ? this.form.value.deadline.getTime() : null
    const keywordDeadline = this.keyword?.deadline ? this.keyword.deadline.getTime() : null

    return formDeadline !== keywordDeadline
  }

  get changingPostLimit(): boolean {
    return this.keyword ? this.keyword.postLimit != this.form.value.postLimit : true
  }

  get hasClaimedFulfilledKeyword(): boolean {
    return !!(
      this.keyword &&
      this.keyword.state === 'fulfilled' &&
      this.keyword.writer &&
      this.keyword.writer.id &&
      !this.duplicateId
    )
  }

  setDeadlineBuffers(): void {
    if (!this.customer) return
    const buffer = this.customer.deadlineBufferHours
    this.minDeadlineDate = getMinDeadlineDate(buffer)
    this.maxDeadlineDate = getMaxDeadlineDate(buffer)
  }

  get canChangePostLimit(): boolean {
    return this.newKeyword() || !['active', 'assigned'].includes(this.keyword.state)
  }

  get creditUsageSummary(): string {
    if (this.keyword && this.customer?.subscription) {
      const { quantityFromCredits, postLimit } = this.keyword

      const staticMessage = `You requested ${postLimit} ${pluralize(
        'piece',
        postLimit
      )} on this topic and paid with`

      const creditSummary =
        quantityFromCredits > 0
          ? `${quantityFromCredits} ${pluralize('credit', quantityFromCredits)}`
          : ''
      const chargeSummary =
        quantityFromCredits < postLimit
          ? `${this.getTotalAmount(postLimit - quantityFromCredits)}`
          : ''

      return `${staticMessage} ${creditSummary}${
        creditSummary && chargeSummary ? ' + ' : ''
      }${chargeSummary}.`
    }
  }

  get postLimitTooltipMessage(): string {
    return this.assignedWriter ? 'A writer can only be assigned one piece per request' : ''
  }
}
